<script setup lang="ts">
import type { TagStep } from '~/ui-blocks/Formalization/Formalization.types'
import CloseIcon from '~icons/material-symbols/close-rounded'
import DoneIcon from '~icons/material-symbols/done-rounded'

const props = defineProps<{
  title: string
  position: string
  openCollapse?: boolean
  tag?: TagStep | null
  blocked?: boolean
  submittedAt?: string | null
  completedAt?: string | null
  reprovedAt?: string | null
  hasShownStatus: boolean
  hasPendingUpdateEquipment?: boolean
}>()

const { t, d } = useI18n()

interface ShowDate {
  label: string
  value: string | number | Date
  isDate?: boolean
}

const showDate = computed<ShowDate | null>(() => {
  if (props.completedAt)
    return { label: t('accordion.date.completedAt'), value: props.completedAt }

  if (props.reprovedAt)
    return { label: t('accordion.date.reprovedAt'), value: props.reprovedAt }

  if (props.submittedAt)
    return { label: t('accordion.date.submittedAt'), value: props.submittedAt }

  if (!props.blocked && (props.completedAt || props.reprovedAt || props.submittedAt || !props.hasShownStatus))
    return { label: t('accordion.date.submittedAt'), value: t('accordion.date.in_progress'), isDate: false }

  return null
})
</script>

<template>
  <div class="sol-accordion-financing">
    <div v-if="!tag?.state" class="step-badge bg-neutral-high-pure">
      <span class="text-white text-[1rem] md:system:text-[1.5rem] font-bold">{{ position }}</span>
    </div>
    <div v-else-if="tag?.state === 'done'" class="step-badge bg-feedback-positive-pure">
      <span class="text-white text-[1rem] md:system:text-[1.5rem] font-bold text-neutral-high-pure"><DoneIcon class="w-6 h-6" /></span>
    </div>
    <div v-else-if="tag?.state === 'pending'" class="step-badge bg-neutral-high-pure border border-feedback-negative-pure">
      <span class="text-white text-[1rem] md:system:text-[1.5rem] font-bold">{{ position }}</span>
    </div>
    <div v-else-if="tag?.state === 'failed'" class="step-badge bg-neutral-high-pure border border-feedback-negative-pure">
      <span class="text-white text-[1rem] md:system:text-[1.5rem] font-bold text-feedback-negative-pure"><CloseIcon class="w-6 h-6" /></span>
    </div>
    <SolAccordion
      id="accordion"
      class="accordion-mobile"
      :class="{ 'pointer-events-none icon-remove': blocked }"
      bg="bg-neutral-high-pure"
      :on-color="false"
      :open="openCollapse ? openCollapse : false"
    >
      <template #title>
        <div class="flex gap-1 sm:system:gap-3 sm:system:items-center w-full whitespace-nowrap flex-col sm:system:flex-row" :class="{ 'opacity-medium': blocked }">
          <div class="flex gap-3 items-center w-full whitespace-nowrap flex-wrap md:system:flex-nowrap">
            {{ title }}
            <SolTag
              v-if="tag"
              :id="tag.id"
              :text="tag.text"
              :variant="tag.variant"
              :size="tag.size"
              :type="tag.type"
            />
            <div class="flex justify-end md:system:w-full">
              <slot name="icon" />
            </div>
          </div>

          <span
            v-if="showDate && !hasPendingUpdateEquipment"
            class="text-micro font-base mr-6"
          >
            {{ showDate.label }} <strong>{{ showDate.isDate === false ? showDate.value : d(showDate.value, 'short') }}</strong>
          </span>
        </div>
      </template>
      <slot><div /></slot>
    </SolAccordion>
  </div>
</template>

<style lang="scss">
.sol-accordion-financing {
  .step-badge {
    @apply w-10 h-10 rounded-full flex items-center justify-center relative top-5 left-4 lg:system:left-8 shadow-weak z-0;
  }

  > .sol-accordion-core{
    .icon-remove svg.icon{
      @apply hidden;
    }
    > .accordion-content{
      @apply p-xs pt-0  bg-neutral-high-pure rounded-lg;
    }
    @apply bg-neutral-high-pure;
    > .summary{
      @apply p-xs bg-neutral-high-pure rounded-lg;
      .container-title {
        width: 100%;
        @apply fonts-heading-h4 md:system:fonts-heading-h3 text-neutral-low-dark;
      }
    }
  }

  > .sol-accordion-core[open]{
    @apply bg-neutral-high-pure;
    > .summary{
      @apply pb-0 bg-neutral-high-pure rounded-lg;
      .container-title {
        @apply fonts-heading-h4 md:system:fonts-heading-h3 text-neutral-low-dark;
      }
    }
  }

  .accordion-mobile {
    @apply w-full rounded-lg bg-neutral-high-pure;
    box-shadow: 0 0 5px rgba(0, 0, 0, .1);
    .container-title,
    .form-client-data,
    .accordion-content div p {
      @apply -mx-4 lg:system:mx-0;
    }
    .resume-container {
      h2 {
        @apply hidden;
      }
      h3 {
        @apply text-left;
      }
    }
    .form-client-data {
      @apply my-sm;
    }
  }

  /* SolSelect não faz teleport. Erguer só o card com select aberto: acima do
     número da etapa seguinte, abaixo do header sticky (z-19). Fechado, sem lift. */
  &:has(.sol-select-core [aria-expanded='true']) {
    position: relative;
    z-index: 18;
  }
}
</style>
